import { styled } from '@mui/system';
import { useViewport } from 'contexts/viewport';
import { isEqual } from 'lodash';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const truncateToMaxLines = (htmlString, maxLines) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
    if (paragraphs.length <= maxLines) {
        return htmlString;
    }

    const truncated = paragraphs
        .slice(0, maxLines - 1)
        .map((p) => p.outerHTML)
        .join('');

    const lastParagraph = paragraphs[maxLines - 1];
    const lastText = lastParagraph?.textContent.trim();

    // append ellipsis inline with the last visible paragraph
    return `${truncated}<p>${lastText} ...</p>`;
};

const ViewMore = memo(
    ({ text, maxLines = 4, textStyles = {}, ...textProps }) => {
        const { t } = useTranslation();

        const textRef = useRef(null);

        const [isExpanded, setIsExpanded] = useState(false);
        const [isOverflowing, setIsOverflowing] = useState(false);
        const [displayText, setDisplayText] = useState(text);

        const toggleExpand = (e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
        };

        const { width } = useViewport();

        useLayoutEffect(() => {
            if (textRef.current) {
                const { clientHeight, scrollHeight } = textRef.current;

                if (!isExpanded) {
                    const truncatedText = truncateToMaxLines(text, maxLines);
                    setDisplayText(truncatedText);
                    setIsOverflowing(scrollHeight > clientHeight + 1);
                } else {
                    setDisplayText(text);
                }
            }
        }, [text, maxLines, width, isExpanded]);

        return (
            <Stack alignItems={'flex-start'} spacing={2}>
                <TextWrapper
                    {...textProps}
                    isExpanded={isExpanded}
                    maxLines={maxLines}
                    textStyles={textStyles}
                    ref={textRef}
                >
                    {displayText}
                </TextWrapper>
                {isOverflowing && (
                    <Button
                        color='neutral'
                        endIcon={isExpanded ? <Icon kind={'remove'} /> : <Icon kind={'add'} />}
                        variant='borderless'
                        onClick={toggleExpand}
                    >
                        {isExpanded ? t('controls.show-less', 'Show Less') : t('controls.show-more', 'Show More')}
                    </Button>
                )}
            </Stack>
        );
    },
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

const TextWrapper = styled(Text)(({ isExpanded, maxLines, textStyles }) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: isExpanded ? 'none' : maxLines,
    wordBreak: 'break-word', // Forces long words to break
    overflowWrap: 'break-word', // Alternative for better compatibility
    ...textStyles,
}));

export default ViewMore;
